/**
 * CollaboratorBadge — one face.
 *
 * WHY IT NORMALISES. Every list that draws an Access column feeds this its own
 * backend shape, and no two agree on the avatar key: templates send
 * `ProfilePicture`, reviews `avatar_url`, files `avatarUrl`, projects
 * `profile_picture`. The badge used to read three of those four, so a row
 * carrying the fourth lost its picture and fell through to Gravatar's grey
 * silhouette. It now hands the row to `accessModel.normalise` — the same
 * function `CollaboratorStack` uses — so the picture, name, email and initials
 * are found under whatever spelling the endpoint used, and a fifth spelling is
 * one line in `accessModel`, not a fix in a dozen lists.
 *
 * Explicitly-passed fields still win: a caller that already resolved the
 * avatar or initials keeps its value.
 */

import React from 'react';
import ProfileImage from '../../Header/ProfileImage';
import { Tooltip } from '@mui/material';
import { normalise } from '../access/accessModel';

interface CollaboratorBadgeProps {
  /**
   * Any backend collaborator shape. The named keys are the ones callers pass
   * explicitly; everything else is resolved through `accessModel`.
   */
  collaborator: {
    name?: string;
    avatarURL?: string | null;
    avatar_url?: string | null;
    profile_picture?: string | null;
    initials?: string;
    email?: string | null;
    active?: string;
    access?: string;
    [key: string]: any;
  };
  /** Avatar size classes, forwarded to ProfileImage (default `h-7 w-7`). */
  size?: string;
}

export const getInitials = (name?: string | null) => {
  if (!name || typeof name !== 'string') return 'U';
  const parts = name.trim().split(' ').filter(p => p.length > 0);
  if (parts.length === 0) return 'U';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const accessDisplayLabel = (access?: string): string => {
  if (!access) return '';
  if (access.toLowerCase() === 'admin') return 'Owner';
  return access.charAt(0).toUpperCase() + access.slice(1);
};

const CollaboratorBadge: React.FC<CollaboratorBadgeProps> = ({ collaborator, size }) => {
  const person = normalise(collaborator);

  // The caller's own value first, then whatever `accessModel` found. `avatarURL`
  // is the badge's historical prop name and is not a key `accessModel` knows.
  const picture = collaborator.avatarURL || person.avatar || null;
  const name = collaborator.name || person.name;
  const initials = collaborator.initials || person.initials;
  const email = collaborator.email ?? (person.email || null);

  const tooltipText = collaborator.access
    ? `${name} · ${accessDisplayLabel(collaborator.access)}`
    : name;

  return (
    <Tooltip title={tooltipText}>
    <div
     className={`relative rounded-full ${
      collaborator.active
        ? "border-2 border-success-500"
        : "border-2 border-transparent"
    }`}
    >
      <ProfileImage
        profilePicture={picture}
        email={email}
        initials={initials}
        name={name}
        size={size}
      />
    </div>
        </Tooltip>
  );
};

export default CollaboratorBadge;
